import CloseRounded from "@mui/icons-material/CloseRounded";
import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { useScrollLock } from "../../../hooks/useScrollLock";
import IconButton from "../iconButton/IconButton";
import Typography from "../typography/Typography";
import styles from "./modalShell.module.scss";

interface ModalShellProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  children?: ReactNode;
  showClose?: boolean;
}

export default function ModalShell({ open, title, onClose, children, showClose = true }: ModalShellProps): JSX.Element | null {
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className={styles.root} onClick={handleBackdropClick}>
      <section className={styles.card} role="dialog" aria-modal="true" aria-label={title}>
        {(title || showClose) && (
          <header className={styles.header}>
            <div className={styles.title}>
              {title && (
                <Typography textStyle="large-semibold" color="titleText">
                  {title}
                </Typography>
              )}
            </div>
            {showClose && <IconButton icon={CloseRounded} label="Close" onClick={onClose} />}
          </header>
        )}
        <div className={styles.body}>{children}</div>
      </section>
    </div>,
    document.body
  );
}
